import React from 'react';
import { Handle, Position } from 'reactflow';
import { Plus, Check } from 'lucide-react';
import { useGetNodeOptionsQuery } from '@frontend/app/hooks';
import {
  ArrayConditionOperators,
  BooleanConditionOperators,
  DateConditionOperators,
  NumberConditionOperators,
  TextConditionOperators,
} from '@frontend/app/types/automations/condition/ConditionOperator';
import {
  ComboOperator,
  ComparisonOperator,
} from '@frontend/app/types/automations/condition/Operator';

import { FieldType } from '@frontend/app/types/globalTypes';
import { upperFirst } from 'lodash';
import moment from 'moment';
import { convertToTitleCase } from '@frontend/app/utils/strings';
import { cn } from '@/shadcn/lib/utils';
import { FlowNodeData } from '../types';
import { Button } from '@/shadcn/components/ui/button';

interface ConditionNodeV2Props {
  data: FlowNodeData;
}

export const ConditionNodeV2: React.FC<ConditionNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    metadata,
    isEditing,
    isSelected,
    onNodeClick,
    onUpdateNode,
    onDeleteNode,
    automationId,
    templateId,
  } = data;

  // Fetch condition options - match V1 pattern exactly
  const {
    data: {
      getNodeOptions: {
        conditionOptions = [],
      } = { conditionOptions: [] },
    } = { getNodeOptions: { conditionOptions: [] } },
  } = useGetNodeOptionsQuery({
    variables: {
      context: {
        automationId,
        templateId,
      },
    },
    skip: !automationId && !templateId,
    fetchPolicy: 'cache-and-network', // Ensure fresh data
  });

  // Render condition tag with proper formatting - match V1 logic exactly
  const renderConditionTag = (condition: any): string => {
    const operator = condition.mappedOperator || condition.op;
    const field = conditionOptions.find((field) => field.id === condition.attribute);
    if (!field) {
      return condition.attribute ? `Condition: ${condition.attribute}` : 'No added conditions.';
    }
    const fieldType = field.attributeType;
    const fieldDisplayName = field.displayName ? upperFirst(field.displayName) : field.id;

    // Get group display name dynamically based on what's shown in the dropdown
    // This matches how ConditionFormV2 displays groups using convertToTitleCase
    const getGroupDisplayName = (group: string): string => {
      return convertToTitleCase(group.replace(/_/g, ' '));
    };

    const groupName = field.group ? getGroupDisplayName(field.group) : '';
    const displayName = groupName ? `${groupName} ${fieldDisplayName}` : fieldDisplayName;
    const value = condition.value;

    const getDisplayOption = () => {
      switch (fieldType) {
        case FieldType.ANNUAL:
        case FieldType.DATE:
          return DateConditionOperators.find((op) => op.value === operator);
        case FieldType.ARRAY:
          return ArrayConditionOperators.find((op) => op.value === operator);
        case FieldType.BOOLEAN:
          return BooleanConditionOperators.find((op) => op.value === operator);
        case FieldType.NUMBER:
        case FieldType.PERCENTAGE:
          return NumberConditionOperators.find((op) => op.value === operator);
        case FieldType.TEXT:
        case FieldType.EMAIL:
          return TextConditionOperators.find((op) => op.value === operator);
        default:
          return null;
      }
    };

    const displayOption = getDisplayOption();
    const inlineText = displayOption?.inlineLabel || '';
    const secondaryInlineText = displayOption?.secondaryInlineLabel || '';

    if (
      operator === ComboOperator.BETWEEN
      && (
        !Array.isArray(value)
        || value.length !== 2
      )
    ) {
      throw new Error('Invalid value for between operator');
    }

    switch (fieldType) {
      case FieldType.ANNUAL:
      case FieldType.DATE: {
        const dateFormat = fieldType === FieldType.DATE ? 'MM/DD/YYYY' : 'MMM D';
        const valueAsDate = moment(value as Date).utc().format(dateFormat);
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComboOperator.NOT_EQUALS:
          case ComparisonOperator.GREATER_THAN:
          case ComparisonOperator.GREATER_THAN_EQUALS:
          case ComparisonOperator.LESS_THAN:
          case ComparisonOperator.LESS_THAN_EQUALS:
            return `${displayName} ${inlineText} ${valueAsDate} ${secondaryInlineText}`;
          case ComboOperator.BETWEEN:
            return `${displayName} ${inlineText} ${moment(value[0]).utc().format(dateFormat)} ${secondaryInlineText} ${moment(value[1]).utc().format(dateFormat)}`;
          case ComparisonOperator.HAS_VALUE:
          case ComboOperator.IS_NULL:
            return `${displayName} ${inlineText}`;
          case ComparisonOperator.MORE_THAN_AGO:
          case ComboOperator.NOT_MORE_THAN_AGO:
          case ComparisonOperator.IN_THE_LAST:
          case ComboOperator.NOT_IN_THE_LAST:
          case ComboOperator.NOT_IN_THE_NEXT:
          case ComparisonOperator.IN_THE_NEXT:
            return `${displayName} ${inlineText} ${value} ${secondaryInlineText}`;
          default:
            throw new Error('Invalid comparison operator for date attribute');
        }
      }
      case FieldType.ARRAY: {
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComboOperator.NOT_EQUALS:
          case ComparisonOperator.CONTAINS:
          case ComboOperator.NOT_CONTAINS:
            if (!Array.isArray(value)) {
              throw new Error('Invalid value for array attribute');
            }
            const formattedValues = value.map((val) => field.choices.find((c) => c.value === val)?.displayName || val).join(', ');
            return `${displayName} ${inlineText} ${formattedValues} ${secondaryInlineText}`;
          case ComboOperator.IS_NULL:
          case ComparisonOperator.HAS_VALUE:
            return `${displayName} ${inlineText}`;
          default:
            throw new Error('Invalid comparison operator for array attribute');
        }
      }
      case FieldType.BOOLEAN: {
        const boolValue = value ? 'True' : 'False';
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComboOperator.NOT_EQUALS:
            return `${displayName} ${inlineText} ${boolValue}`;
          case ComparisonOperator.HAS_VALUE:
          case ComboOperator.IS_NULL:
            return `${displayName} ${inlineText}`;
          default:
            console.warn('Unknown boolean operator, using fallback:', operator);
            return `${displayName} is ${boolValue}`;
        }
      }
      case FieldType.NUMBER:
      case FieldType.PERCENTAGE:
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComparisonOperator.GREATER_THAN:
          case ComparisonOperator.GREATER_THAN_EQUALS:
          case ComparisonOperator.LESS_THAN:
          case ComparisonOperator.LESS_THAN_EQUALS:
          case ComboOperator.NOT_EQUALS:
            return `${displayName} ${inlineText} ${value}`;
          case ComboOperator.BETWEEN:
            return `${displayName} ${inlineText} ${value[0]} ${secondaryInlineText} ${value[1]}`;
          case ComboOperator.IS_NULL:
          case ComparisonOperator.HAS_VALUE:
            return `${displayName} ${inlineText}`;
          default:
            throw new Error('Invalid comparison operator for number attribute');
        }
      case FieldType.TEXT:
      case FieldType.EMAIL:
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComparisonOperator.CONTAINS:
          case ComboOperator.NOT_CONTAINS:
          case ComboOperator.NOT_EQUALS:
            return `${displayName} ${inlineText} ${value} ${secondaryInlineText}`;
          case ComboOperator.IS_NULL:
          case ComparisonOperator.HAS_VALUE:
            return `${displayName} ${inlineText}`;
          default:
            throw new Error('Invalid comparison operator for text attribute');
        }
      default:
        console.warn('Unknown field type, using fallback display:', fieldType);
        // Fallback for unknown field types
        if (operator === ComboOperator.IS_NULL) {
          return `${displayName} is null`;
        } else if (operator === ComparisonOperator.HAS_VALUE) {
          return `${displayName} has value`;
        } else if (typeof value === 'boolean') {
          return `${displayName} is ${value ? 'True' : 'False'}`;
        } else if (value !== null && value !== undefined) {
          return `${displayName} ${inlineText || 'equals'} ${value}`;
        } else {
          return `${displayName} ${inlineText || 'condition'}`;
        }
    }
  };

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteNode?.(nodeId);
  };

  // Support both V1 (metadata.children) and V2 (metadata.condition) formats
  const conditions = metadata?.children || [];
  const singleCondition = metadata?.condition;
  const hasConditions = conditions.length > 0 || singleCondition;

  return (
    <div className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Main node container - Figma style */}
      <div
        className={cn(
          'w-64 bg-white border border-gray-200 rounded-2xl shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300',
          isSelected && 'ring-2 ring-blue-500 ring-offset-2 border-blue-300'
        )}
        onClick={handleNodeClick}
      >
        <div className="p-4">
          {/* Header with icon and title */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                <Check className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-sm">Condition</h3>
              </div>
            </div>

            {isEditing && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-gray-100"
                onClick={handleDelete}
              >
                <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            )}
          </div>

          {/* Content area */}
          <div className="space-y-3">
            {hasConditions ? (
              <div className="space-y-2">
                {conditions.length > 0 ? (
                  conditions.map((condition: any, index: number) => (
                    <div
                      key={condition.uuid || index}
                      className="bg-gray-100 rounded-lg px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-150"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isEditing) {
                          onUpdateNode?.(nodeId, {
                            metadata: {
                              ...metadata,
                              condition,
                            },
                          });
                          onNodeClick?.(nodeId);
                        }
                      }}
                    >
                      <span className="text-sm text-gray-700">
                        {(() => {
                          try {
                            return renderConditionTag(condition);
                          } catch (error) {
                            return `Condition ${index + 1}`;
                          }
                        })()}
                      </span>
                      {isEditing && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-gray-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNodeClick?.(nodeId);
                          }}
                        >
                          <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </Button>
                      )}
                    </div>
                  ))
                ) : singleCondition ? (
                  <div
                    className="bg-gray-100 rounded-lg px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-150"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isEditing) {
                        onUpdateNode?.(nodeId, {
                          metadata: {
                            ...metadata,
                            condition: singleCondition,
                          },
                        });
                        onNodeClick?.(nodeId);
                      }
                    }}
                  >
                    <span className="text-sm text-gray-700">
                      {(() => {
                        try {
                          return renderConditionTag(singleCondition);
                        } catch (error) {
                          return 'Condition configured';
                        }
                      })()}
                    </span>
                    {isEditing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 hover:bg-gray-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNodeClick?.(nodeId);
                        }}
                      >
                        <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 bg-transparent hover:bg-gray-50"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateNode?.(nodeId, {
                    metadata: {
                      ...metadata,
                      condition: null,
                    },
                  });
                  onNodeClick?.(nodeId);
                }}
              >
                <Plus className="h-3 w-3 mr-2" />
                Add Condition
              </Button>
            )}

            {/* Add more conditions button */}
            {hasConditions && isEditing && (
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 bg-transparent hover:bg-gray-50"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateNode?.(nodeId, {
                    metadata: {
                      ...metadata,
                      condition: null,
                    },
                  });
                  onNodeClick?.(nodeId);
                }}
              >
                <Plus className="h-3 w-3 mr-2" />
                Add Another Condition
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Output handles for branching */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        isConnectable={false}
        className="w-3 h-3 bg-green-500 border-2 border-white"
        style={{ left: '25%' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        isConnectable={false}
        className="w-3 h-3 bg-red-500 border-2 border-white"
        style={{ left: '75%' }}
      />

      {/* Add buttons positioned at bottom for True/False paths */}
      {isEditing && (
        <>
          {/* True branch button - always show for conditions */}
          <div className="absolute top-full left-1/4 transform -translate-x-1/2 mt-4 z-20">
            <Button
              variant="outline"
              size="sm"
              className="w-8 h-8 p-0 rounded-full bg-white border border-gray-300 hover:border-green-400 hover:bg-green-50 shadow-md"
              onClick={(e) => {
                e.stopPropagation(); // Prevent node selection
                const rect = e.currentTarget.getBoundingClientRect();
                data.onShowOptionBar?.(nodeId, {
                  x: rect.left + rect.width / 2,
                  y: rect.top
                }, false); // False = True branch (not else branch)
              }}
            >
              <Plus className="h-4 w-4 text-gray-600" />
            </Button>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 text-xs text-green-600 whitespace-nowrap">
              True
            </div>
          </div>

          {/* False branch button - show for all CONDITION nodes */}
          <div className="absolute top-full left-3/4 transform -translate-x-1/2 mt-4 z-20">
            <Button
              variant="outline"
              size="sm"
              className="w-8 h-8 p-0 rounded-full bg-white border border-gray-300 hover:border-red-400 hover:bg-red-50 shadow-md"
              onClick={(e) => {
                e.stopPropagation(); // Prevent node selection
                const rect = e.currentTarget.getBoundingClientRect();
                data.onShowOptionBar?.(nodeId, {
                  x: rect.left + rect.width / 2,
                  y: rect.top
                }, true); // True = Else branch
              }}
            >
              <Plus className="h-4 w-4 text-gray-600" />
            </Button>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 text-xs text-red-600 whitespace-nowrap">
              False
            </div>
          </div>
        </>
      )}
    </div>
  );
};
